import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import * as E2eManager from "../../services/e2e/e2e_manager"
import { dsConfirm } from "../../utils/ds_confirm";

/**
 * Stimulus controller for chat page functionality.
 *
 * Handles auto-scrolling, file previews, reply citations,
 * message deletion, keyboard shortcuts, and E2E decryption.
 */
export default class extends Controller {
  static targets = [
    "messages", "input", "form", "fileInput", "filePreview",
    "replyPreview", "replySender", "replyBody", "replyToId",
    "typingIndicator", "typingText",
  ]
  static values = {
    conversationId: Number,
    deleteUrl: String,
    deleteConfirm: String,
    currentUserId: Number,
    typingText: String,
  }

  connect() {
    this.scrollToBottom()
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-end", this._onSubmitEnd = (e) => {
        if (e.detail.success) this.resetForm()
      })
    }

    // Subscribe to ConversationChannel for heartbeat / presence / read receipts
    const consumer = window._cableConsumer;
    if (consumer && this.conversationIdValue) {
      this.cableSub = consumer.subscriptions.create(
        { channel: "ConversationChannel", id: this.conversationIdValue },
        {
          received: (data) => this.handleChannelData(data),
        }
      );
      window._conversationSubscription = this.cableSub;
    }

    // Initialize E2E decryption
    this._initE2E()
  }

  disconnect() {
    if (this.hasFormTarget && this._onSubmitEnd) {
      this.formTarget.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    }

    if (this.cableSub) {
      this.cableSub.unsubscribe();
      window._conversationSubscription = null;
    }

    if (this._e2eObserver) {
      this._e2eObserver.disconnect()
      this._e2eObserver = null
    }

    clearTimeout(this._typingTimeout)
  }

  // ── E2E Decryption ──

  async _initE2E() {
    try {
      // Initialize libsodium WASM
      await E2eManager.initialize()

      // Scope E2E key store to the authenticated user
      if (!this.currentUserIdValue) return
      E2eManager.setCurrentUser(this.currentUserIdValue)

      const ready = await E2eManager.ensureKeyPairExists()
      if (!ready) return

      if (!this.conversationIdValue) return

      this._convKey = await E2eManager.getConversationKey(this.conversationIdValue)
      if (!this._convKey) return

      // Decrypt all visible E2E messages
      await this._decryptVisibleMessages()

      // Watch for new messages appended via Turbo Streams
      this._observeNewMessages()
    } catch (err) {
      console.warn("[E2E] init failed:", err)
    }
  }

  async _decryptVisibleMessages() {
    if (!this._convKey || !this.hasMessagesTarget) return

    const elements = this.messagesTarget.querySelectorAll('[data-e2e="true"]')
    const promises = Array.from(elements).map((el) => this._decryptElement(el))
    await Promise.allSettled(promises)
  }

  async _decryptElement(el) {
    // Skip already-decrypted elements
    if (el.dataset.e2eDecrypted === "true") return

    const encryptedBody = el.dataset.encryptedBody
    const nonce = el.dataset.messageNonce
    const encryptionVersion = parseInt(el.dataset.encryptionVersion || "0", 10)

    if (!encryptedBody || !nonce || !this._convKey) return

    // V1 messages cannot be decrypted with V2 crypto — show placeholder
    if (encryptionVersion < 2) return

    const plaintext = await E2eManager.decryptMessageBody(
      encryptedBody, nonce, this._convKey,
    )

    if (plaintext !== null) {
      // Replace placeholder with decrypted text
      el.textContent = plaintext
      el.dataset.e2eDecrypted = "true"
    }
  }

  _observeNewMessages() {
    if (!this.hasMessagesTarget || this._e2eObserver) return

    this._e2eObserver = new MutationObserver((mutations) => {
      let hasNewMessage = false

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue

          hasNewMessage = true

          // Check the added node itself and its descendants
          const e2eElements = node.querySelectorAll
            ? node.querySelectorAll('[data-e2e="true"]')
            : []

          if (node.matches?.('[data-e2e="true"]')) {
            this._decryptElement(node)
          }

          for (const el of e2eElements) {
            this._decryptElement(el)
          }
        }
      }

      // Hide typing indicator when a new message arrives
      if (hasNewMessage) this._hideTypingIndicator()
    })

    this._e2eObserver.observe(this.messagesTarget, {
      childList: true,
      subtree: true,
    })
  }

  // ── Existing functionality ──

  // Append a new message to the chat
  appendMessage(message) {
    if (!this.hasMessagesTarget) return

    // Check if message already exists (from Turbo Stream)
    if (document.getElementById(`message_${message.id}`)) return

    const isMe = this.isSentByMe(message)
    const html = this.buildMessageHTML(message, isMe)
    this.messagesTarget.insertAdjacentHTML("beforeend", html)
  }

  isSentByMe(_message) {
    // Server handles sender detection via Turbo Streams
    return false
  }

  buildMessageHTML(message, isMe) {
    const align = isMe ? "justify-end" : "justify-start"
    const bg = isMe ? "bg-[#3F4E4F] text-white" : "bg-white"
    const timeColor = isMe ? "text-gray-300" : "text-gray-400"
    const time = new Date(message.created_at).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })

    return `
      <div class="flex ${align} mb-3" id="message_${message.id}">
        <div class="max-w-[70%] ${bg} rounded-[15px] shadow-sm px-4 py-3">
          ${!isMe ? `<p class="text-xs font-semibold text-[#3F4E4F] mb-1">${this.escapeHTML(message.sender_name)}</p>` : ""}
          <p class="text-sm whitespace-pre-wrap break-words">${this.escapeHTML(message.body)}</p>
          <p class="text-[10px] mt-1 ${timeColor} text-right">${time}</p>
        </div>
      </div>`
  }

  // Auto-scroll to bottom of messages
  scrollToBottom() {
    if (!this.hasMessagesTarget) return

    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  // Scroll to a specific message (used for reply citations)
  scrollToMessage(event) {
    const messageId = event.currentTarget.dataset.messageId
    const el = document.getElementById(`message_${messageId}`)
    if (!el) return

    el.scrollIntoView({ behavior: "smooth", block: "center" })
    el.classList.add("ring-2", "ring-[#3F4E4F]/30", "rounded-[15px]")
    setTimeout(() => {
      el.classList.remove("ring-2", "ring-[#3F4E4F]/30", "rounded-[15px]")
    }, 2000)
  }

  // Set up reply citation in the form
  reply(event) {
    const btn = event.currentTarget
    const messageId = btn.dataset.messageId
    const senderName = btn.dataset.senderName
    const messageBody = btn.dataset.messageBody

    if (this.hasReplyPreviewTarget) {
      this.replyPreviewTarget.classList.remove("hidden")
    }
    if (this.hasReplySenderTarget) {
      this.replySenderTarget.textContent = senderName
    }
    if (this.hasReplyBodyTarget) {
      this.replyBodyTarget.textContent = messageBody
    }
    if (this.hasReplyToIdTarget) {
      this.replyToIdTarget.value = messageId
    }

    // Focus the input
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  // Cancel reply citation
  cancelReply() {
    if (this.hasReplyPreviewTarget) {
      this.replyPreviewTarget.classList.add("hidden")
    }
    if (this.hasReplyToIdTarget) {
      this.replyToIdTarget.value = ""
    }
  }

  // Hide a form panel (used by cancel buttons)
  hideForm({ params: { formId } }) {
    const el = document.getElementById(formId)
    if (el) el.classList.add("hidden")
  }

  // Delete a message via POST
  async deleteMessage(event) {
    const messageId = event.currentTarget.dataset.messageId
    if (!messageId) return

    const confirmText = this.deleteConfirmValue
    if (!(await dsConfirm(confirmText, { tone: "danger" }))) return

    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content
    const deleteUrl = this.deleteUrlValue

    try {
      const response = await fetch(deleteUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html, text/html",
        },
        body: `message_id=${messageId}`,
      })

      if (response.ok) {
        const contentType =
          response.headers.get("content-type") || ""
        if (contentType.includes("turbo-stream")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        } else {
          Turbo.visit(window.location.href, {
            action: "replace",
          })
        }
      }
    } catch {
      // Silently fail — the server broadcast will
      // update the UI
    }
  }

  // Handle keyboard shortcuts (Enter to send, Shift+Enter for newline)
  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      const hasText = this.hasInputTarget && this.inputTarget.value.trim()
      const hasFiles = this.hasFileInputTarget && this.fileInputTarget.files.length > 0
      if (this.hasFormTarget && (hasText || hasFiles)) {
        this.formTarget.requestSubmit()
      }
    }
  }

  // Reset form inputs after submission
  resetForm() {
    this.inputTarget.value = ""
    this.autoResize()
    this.cancelReply()
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.innerHTML = ""
      this.filePreviewTarget.classList.add("hidden")
    }
  }

  // Auto-resize textarea
  autoResize() {
    if (!this.hasInputTarget) return

    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
  }

  // Preview selected files
  previewFiles() {
    if (!this.hasFileInputTarget || !this.hasFilePreviewTarget) return

    const files = this.fileInputTarget.files
    this.filePreviewTarget.classList.toggle("hidden", files.length === 0)
    this.filePreviewTarget.innerHTML = ""

    Array.from(files).forEach((file) => {
      const tag = document.createElement("span")
      tag.className =
        "bg-gray-100 rounded px-2 py-1 text-xs text-gray-600 flex items-center gap-1"
      tag.textContent = file.name
      this.filePreviewTarget.appendChild(tag)
    })
  }

  // Handle data received from ConversationChannel (read receipts, typing, etc.)
  handleChannelData(data) {
    if (data.action === "messages_read") {
      this.updateCheckmarksToRead()
    } else if (data.type === "typing") {
      this._showTypingIndicator(data.user_id)
    }
  }

  // ── Typing indicator ──

  // Show the typing indicator when another user is typing
  _showTypingIndicator(userId) {
    // Ignore our own typing events
    if (userId === this.currentUserIdValue) return

    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.remove("hidden")
    }
    if (this.hasTypingTextTarget) {
      this.typingTextTarget.textContent = this.typingTextValue || "is typing..."
    }

    // Auto-hide after 4 seconds (reset timer on each new event)
    clearTimeout(this._typingTimeout)
    this._typingTimeout = setTimeout(() => this._hideTypingIndicator(), 4000)
  }

  _hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add("hidden")
    }
    clearTimeout(this._typingTimeout)
  }

  // Send typing event via ActionCable (debounced to 3 seconds)
  sendTypingEvent() {
    if (!this.cableSub) return

    const now = Date.now()
    if (this._lastTypingSent && now - this._lastTypingSent < 3000) return

    this._lastTypingSent = now
    this.cableSub.perform("typing")
  }

  // Update all single-check (✓ gray) SVGs to double-check (✓✓ blue)
  // when the recipient has read our messages.
  updateCheckmarksToRead() {
    if (!this.hasMessagesTarget) return

    // Find all single-check SVGs (gray, sent but unread)
    const singleChecks = this.messagesTarget.querySelectorAll(
      'svg.text-gray-300[viewBox="0 0 16 14"]'
    )

    singleChecks.forEach((svg) => {
      // Replace with double-check (blue, read)
      const doubleCheck = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      doubleCheck.setAttribute("class", "w-4 h-3 text-blue-400 inline-block")
      doubleCheck.setAttribute("viewBox", "0 0 24 14")
      doubleCheck.setAttribute("fill", "none")
      doubleCheck.setAttribute("stroke", "currentColor")
      doubleCheck.setAttribute("stroke-width", "2.5")
      doubleCheck.setAttribute("stroke-linecap", "round")
      doubleCheck.setAttribute("stroke-linejoin", "round")

      const path1 = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path1.setAttribute("d", "M1 7l4 4L13 3")
      const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path2.setAttribute("d", "M7 7l4 4L19 3")

      doubleCheck.appendChild(path1)
      doubleCheck.appendChild(path2)
      svg.replaceWith(doubleCheck)
    })
  }

  // Escape HTML to prevent XSS
  escapeHTML(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
